import { useCallback, useEffect, useState } from "react";
import { useUnityContext, Unity } from "react-unity-webgl";

const fetchFunction = async (API_KEY: string, text: string) => {
  const response = await fetch(
    "https://api.vsllabs.com/api/v2/models/clerc/0",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
      },
      body: JSON.stringify({
        user_input: text,
        settings_nlp: {
          synonyms: false,
          details: false,
          ner: false,
          numbers: false,
          nmm: false,
          emotions: false,
          translation_model: {
            model_type: "SEQ2SEQ",
            model_id: "",
          },
        },
      }),
    }
  );

  return await response.json();
};

type useVslWebGLProps = {
  API_KEY: string;
  loaderUrl: string;
  dataUrl: string;
  frameworkUrl: string;
  codeUrl: string;
  streaming_mode?: boolean;
};

function handleCacheControl(url: string) {
  if (url.endsWith(".loader.js") || url.endsWith(".framework.js.br")) {
    return "no-store";
  }
  return "must-revalidate";
}

const useVslWebGL = ({
  API_KEY,
  loaderUrl,
  dataUrl,
  frameworkUrl,
  codeUrl,
  streaming_mode = false,
}: useVslWebGLProps) => {
  const [translatedText, setTranslatedText] = useState("");
  const [streamingTranslatedText, setStreamingTranslatedText] = useState("");
  const [streamTextArr, setStreamTextArr] = useState<string[]>([]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [error, setError] = useState("");

  const { unityProvider, sendMessage, isLoaded } = useUnityContext({
    loaderUrl,
    dataUrl,
    frameworkUrl,
    codeUrl,
    cacheControl: handleCacheControl,
  });

  const translateTextHelper = useCallback(
    async (text: string, isStreamTranslation: boolean) => {
      try {
        !isStreamTranslation && setTranslatedText("");
        setIsTranslating(true);
        const data = await fetchFunction(API_KEY, text);
        isStreamTranslation
          ? setStreamingTranslatedText(data?.NLP?.unity_3d_input || "")
          : setTranslatedText(data?.NLP?.unity_3d_input || "");
        setIsTranslating(false);
        setError("");
      } catch (_err) {
        setError("Invalid API Key!");
        console.error("Invalid API Key!");
      }
    },
    [API_KEY]
  );

  const translateTextToASL = useCallback(
    async (text: string) => {
      if (isLoaded && isApproved && text) {
        if (!streaming_mode) {
          await translateTextHelper(text, false);
        } else {
          setStreamTextArr((prev) => [...prev, text]);
        }
      }
    },
    [isLoaded, API_KEY, isApproved, streaming_mode]
  );

  const replay = useCallback(() => {
    if (translatedText && !streaming_mode) {
      sendMessage("Gabi", "GetWordsFromUser", translatedText);
    } else {
      setError(
        "Cannot reply, either no text provided or streaming mode is enabled."
      );
    }
  }, [translatedText, streaming_mode]);

  const changeBgColor = useCallback((color: string) => {
    if (color) {
      sendMessage("BGcontroller", "ChangeColorFromUserInput", color);
    }
  }, []);

  const toggleCameraRotation = useCallback((isEnabled: boolean) => {
    sendMessage(
      "Main Camera",
      "ToggleRotationFromStringInput",
      isEnabled ? "true" : "false"
    );
  }, []);

  const setAnimationSpeed = useCallback((speed: string) => {
    sendMessage("speedController", "SetSpeedFromUserInput", speed);
  }, []);

  useEffect(() => {
    if (API_KEY) {
      (async () => {
        try {
          await fetchFunction(API_KEY, "test");
          setIsApproved(true);
        } catch (_err) {
          setError("Invalid API Key!");
          console.error("Invalid API Key!");
        }
      })();
    } else {
      setError("Missing API_KEY!");
      console.error("Missing API_KEY!");
    }
  }, [API_KEY]);

  useEffect(() => {
    if (translatedText) {
      sendMessage("Gabi", "GetWordsFromUser", translatedText);
    }
  }, [translatedText]);

  useEffect(() => {
    if (streamingTranslatedText) {
      sendMessage("Gabi", "GetWordsFromUserStream", streamingTranslatedText);
    }
  }, [streamingTranslatedText]);

  useEffect(() => {
    if (!isTranslating && streamTextArr.length) {
      (async () => {
        const textToTranslate = streamTextArr[0];
        setStreamTextArr((prev) => prev.slice(1));
        await translateTextHelper(textToTranslate, true);
      })();
    }
  }, [streamTextArr.length, isTranslating]);

  return {
    VSLWebGl: Unity,
    translateTextToASL,
    isUnityLoaded: isLoaded,
    isTranslating,
    unityProvider,
    replay,
    error,
    changeBgColor,
    toggleCameraRotation,
    setAnimationSpeed,
  };
};
export default useVslWebGL;
